import React, { useMemo } from "react";
import { cn } from "./ui/analysisTableTheme.js";

function formatCount(value, language = "de") {
  return new Intl.NumberFormat(language === "de" ? "de-DE" : "en-US").format(Number(value || 0));
}

function txLabel(count, language = "de") {
  if (language === "de") return Number(count || 0) === 1 ? "Transaktion" : "Transaktionen";
  return Number(count || 0) === 1 ? "transaction" : "transactions";
}

/** Timestamp zu Sekunden (einheitlich für Jahres-Vergleiche) */
export function emptyStateToSec(ts) {
  const n = Number(ts);
  return n > 1e12 ? Math.floor(n / 1000) : n;
}

/**
 * Einheitliche Kennzahlen für leere Tabellen (Backend leer / falsches Jahr / Filter).
 */
export function computeTableEmptyMetrics({
  allRows,
  yearRows,
  filteredRows,
  effectiveYear,
}) {
  const totalCount = Array.isArray(allRows) ? allRows.length : 0;
  const yearCount = Array.isArray(yearRows) ? yearRows.length : 0;
  const y = String(effectiveYear || "").trim();
  let otherYearsCount = 0;
  if (y && Array.isArray(allRows)) {
    otherYearsCount = allRows.filter((tx) => {
      const ts = tx?.timestamp;
      if (ts == null || ts === "") return false;
      const yy = new Date(emptyStateToSec(ts) * 1000).getFullYear().toString();
      return yy !== y;
    }).length;
  }
  const fr = Array.isArray(filteredRows) ? filteredRows.length : 0;
  const hiddenByFilterCount = yearCount > 0 && fr === 0 ? yearCount : 0;
  return { totalCount, yearCount, otherYearsCount, hiddenByFilterCount };
}

function buildCopy({
  language,
  year,
  totalCount,
  yearCount,
  otherYearsCount,
  hiddenByFilterCount,
}) {
  const hasYear = !!String(year || "").trim();

  if (totalCount <= 0) {
    return {
      title:
        language === "de"
          ? hasYear
            ? `Keine Transaktionen für das Steuerjahr ${year}`
            : "Keine Transaktionen gefunden"
          : hasYear
            ? `No transactions for tax year ${year}`
            : "No transactions found",
      description:
        language === "de"
          ? "Für diese Tabelle wurden aktuell keine Backend-Daten empfangen."
          : "No backend data has been received for this table yet.",
    };
  }

  if (hasYear && yearCount <= 0 && otherYearsCount > 0) {
    return {
      title:
        language === "de"
          ? `Keine Transaktionen für das Steuerjahr ${year}`
          : `No transactions for tax year ${year}`,
      description:
        language === "de"
          ? `${formatCount(otherYearsCount, language)} ${txLabel(otherYearsCount, language)} sind nur in anderen Jahren vorhanden.`
          : `${formatCount(otherYearsCount, language)} ${txLabel(otherYearsCount, language)} exist in other years only.`,
    };
  }

  if (hiddenByFilterCount > 0) {
    return {
      title:
        language === "de"
          ? "Keine Treffer für die aktuelle Auswahl"
          : "No matches for the current selection",
      description:
        language === "de"
          ? hasYear
            ? `${formatCount(yearCount, language)} ${txLabel(yearCount, language)} sind für ${year} vorhanden, werden aber aktuell durch Suche oder Filter ausgeblendet.`
            : `${formatCount(totalCount, language)} ${txLabel(totalCount, language)} sind vorhanden, werden aber aktuell ausgeblendet.`
          : hasYear
            ? `${formatCount(yearCount, language)} ${txLabel(yearCount, language)} are available for ${year}, but currently hidden by search or filters.`
            : `${formatCount(totalCount, language)} ${txLabel(totalCount, language)} are available, but currently hidden.`,
    };
  }

  return {
    title:
      language === "de"
        ? hasYear
          ? `Keine Transaktionen für das Steuerjahr ${year}`
          : "Keine Transaktionen gefunden"
        : hasYear
          ? `No transactions for tax year ${year}`
          : "No transactions found",
    description:
      language === "de"
        ? "Aktuell gibt es für diese Ansicht keine passenden Einträge."
        : "There are currently no matching entries for this view.",
  };
}

export default function UnifiedTableEmptyState({
  language = "de",
  year = "",
  scopeLabel = "",
  totalCount = 0,
  yearCount = 0,
  otherYearsCount = 0,
  hiddenByFilterCount = 0,
  title,
  description,
  className = "",
  inTable = false,
  compact = false,
}) {
  const copy = useMemo(() => {
    let base;
    if (title || description) {
      base = {
        title: title || (language === "de" ? "Keine Transaktionen gefunden" : "No transactions found"),
        description: description || "",
      };
    } else {
      base = buildCopy({
        language,
        year,
        totalCount,
        yearCount,
        otherYearsCount,
        hiddenByFilterCount,
      });
    }
    if (scopeLabel && base.title) {
      base = { ...base, title: `${scopeLabel} · ${base.title}` };
    }
    return base;
  }, [
    title,
    description,
    scopeLabel,
    language,
    year,
    totalCount,
    yearCount,
    otherYearsCount,
    hiddenByFilterCount,
  ]);

  return (
    <div
      className={cn(
        "w-full",
        className
      )}
    >
      <div
        className={cn(
          "relative overflow-hidden rounded-[18px] border border-slate-200/95",
          "bg-[linear-gradient(180deg,rgba(255,255,255,0.985)_0%,rgba(248,250,252,0.985)_100%)]",
          "shadow-[0_10px_30px_-26px_rgba(37,99,235,0.18)]",
          "w-full",
          compact ? "px-5 py-6" : "px-7 py-8"
        )}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-sky-400/0 via-indigo-400/60 to-violet-400/0" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(99,102,241,0.035),transparent_38%)]" />

        <div className="relative z-10 flex flex-col items-center text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-[inset_0_1px_0_rgba(255,255,255,0.92)]">
            <div className="flex h-4.5 w-4.5 items-center justify-center rounded-md border border-indigo-200 bg-indigo-50">
              <div className="h-2.5 w-2.5 rotate-45 rounded-[2px] border border-indigo-600/80 bg-white" />
            </div>
          </div>

          <h3 className="mt-4 text-[15px] font-semibold tracking-[0.01em] text-slate-900 sm:text-[16px]">
            {copy.title}
          </h3>

          {copy.description ? (
            <p className="mt-2 max-w-[760px] text-[13px] leading-5 text-slate-500 sm:text-[13.5px]">
              {copy.description}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
